import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer } from "lucide-react";
import Logo from "@/components/Logo";
import { DraftBanner, SourcePill, StatusPill } from "@/components/forms/FormBadges";

function Blank({ f }) {
  if (f.type === "rating") return (
    <div className="flex gap-2">{[1, 2, 3, 4, 5].map((n) => <span key={n} className="w-7 h-7 border border-slate-400 rounded flex items-center justify-center text-xs">{n}</span>)}</div>
  );
  if (f.type === "checkbox") return <span className="w-4 h-4 border border-slate-500 inline-block align-middle" />;
  if (f.type === "select") return <div className="text-xs text-slate-600">☐ {f.options.join("   ☐ ")}</div>;
  if (f.type === "textarea") return <div className="h-20 border border-slate-300 rounded" />;
  return <div className="h-7 border-b border-slate-400" />;
}

export default function FormPrint() {
  const { templateId } = useParams();
  const [t, setT] = useState(null);
  useEffect(() => { api.get(`/forms/templates/${templateId}`).then((r) => setT(r.data)).catch(() => setT({ error: true })); }, [templateId]);
  if (t?.error) return <div className="p-10 text-center text-slate-500">This form is not available to you.</div>;
  if (!t) return <div className="p-10 text-center text-slate-500">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="no-print flex items-center justify-between mb-4">
        <Link to="/app/forms" data-testid="back-to-forms" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#0A2A5E]"><ArrowLeft className="w-4 h-4" /> All forms</Link>
        <Button data-testid="print-blank-btn" onClick={() => window.print()} className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white"><Printer className="w-4 h-4 mr-2" /> Print blank form</Button>
      </div>
      {t.banner && <div className="mb-4"><DraftBanner text={t.banner} status={t.status} /></div>}
      <Card data-testid="print-template" className="print-area p-6 sm:p-10 bg-white border-slate-200">
        <div className="flex items-center justify-between border-b border-slate-200 pb-4">
          <div className="flex items-center gap-3">
            <Logo size={40} />
            <div>
              <div className="font-display font-extrabold text-[#0A2A5E]">LUMIS HR</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#C7761A] font-semibold">{t.category}</div>
            </div>
          </div>
          <div className="text-right text-xs text-slate-500"><div>Date: ______________</div><div className="mt-1 flex gap-1 justify-end"><SourcePill source={t.source_kind} label={t.source_label} /><StatusPill status={t.status} label={t.status_label} /></div><div className="mt-1">Version {t.version}</div></div>
        </div>
        <h1 className="font-display text-2xl font-bold text-[#0A2A5E] mt-5">{t.title}</h1>
        <p className="text-sm text-slate-600">{t.description}</p>
        {t.bands && (
          <p className="text-xs text-slate-500 mt-2">Rating scale: 1 Unsatisfactory · 2 Needs improvement · 3 Fully successful · 4 Exceeds · 5 Outstanding. Weighted % = Σ(rating × weight) ÷ Σ(5 × weight). Bands: {t.bands.map(([c, l]) => `${l} ≥ ${c}%`).join(" · ")}</p>
        )}
        <div className="mt-6 space-y-6">
          {t.sections.map((sec, i) => (
            <div key={sec.title || i} className="print-break">
              <h2 className="font-display font-bold text-[#0A2A5E] mb-3 flex items-center gap-2"><span className="w-1.5 h-4 rounded-full bg-[#F5A623]" />{sec.title}</h2>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
                {sec.fields.map((f) => (
                  <div key={f.id} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                    <div className="text-xs font-medium text-slate-700 mb-1">{f.label}{f.help && <span className="ml-2 text-[#C7761A]">{f.help}</span>}</div>
                    <Blank f={f} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        {t.source && <div className="mt-8 pt-4 border-t border-slate-200 text-xs text-slate-500">Reference guide: {t.source.citation} — {t.source.source_url}</div>}
        <div className="mt-6 pt-3 border-t border-slate-200 text-[10px] text-slate-500 leading-relaxed" data-testid="print-disclaimer"><p>{t.disclaimer}</p><p className="mt-1">{t.source_label} · Created by LUMIS HR Solutions LLC · {t.copyright}</p></div>
      </Card>
    </div>
  );
}
